import type { MaybeRefOrGetter } from 'vue';
import type { FieldFilter, PrimitiveType } from '#layers/data-model/public';
import type { UIFilterOperator } from '#layers/filters/components/Filter/utils/filter-ui-operator';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterOperator, getFieldFilterOperatorName, getPrimitiveFieldPresentationDefaults, isFilterOperator } from '#layers/data-model/public';

export type SupportedOperatorsSpec =
	| FilterOperator[]
	| ((operators: FilterOperator[]) => FilterOperator[]);

export interface OperatorResolutionOptions {
	mode?: 'default' | 'advanced';
}

export function useFilterOperator(filter: MaybeRefOrGetter<FieldFilter>) {
	const { t } = useI18n();

	const operator = computed(() => getFieldFilterOperatorName(toValue(filter)));

	return {
		operator,
		getOperatorLabel,
	};

	function getOperatorLabel(operator: UIFilterOperator, operatorLabels?: Partial<Record<FilterOperator, string>>) {
		const override = isFilterOperator(operator) ? operatorLabels?.[operator] : undefined;
		return override ? t(override) : t(`filters.operators.${operator}`);
	}
}

export function getSupportedOperators(type: PrimitiveType): FilterOperator[] {
	return getPrimitiveFieldPresentationDefaults(type).filter?.supportedOperators ?? [];
}

export function getDefaultOperators(type: PrimitiveType): FilterOperator[] {
	const filter = getPrimitiveFieldPresentationDefaults(type).filter;
	return filter?.defaultOperators ?? filter?.supportedOperators ?? [];
}

export function narrowOperators(
	baseOperators: FilterOperator[],
	spec?: SupportedOperatorsSpec,
): FilterOperator[] {
	if (!spec) return baseOperators;
	if (Array.isArray(spec)) return baseOperators.filter((op) => spec.includes(op));
	return spec(baseOperators);
}

export function getNarrowedOperatorsForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
	options: OperatorResolutionOptions = {},
): FilterOperator[] {
	if (!field) return [];
	const baseOps = options.mode === 'advanced' ? getSupportedOperators(field.type) : getDefaultOperators(field.type);
	return narrowOperators(baseOps, spec);
}

/**
 * Picks the seeded default operator from a narrowed list. Honors an explicit `defaultOperator`
 * only when it is present in `operators` (defensive against config/operator drift); otherwise
 * falls back to the first operator, then to `FilterOperator.EQ`.
 */
export function resolveDefaultOperator(
	operators: FilterOperator[],
	defaultOperator?: FilterOperator,
): FilterOperator {
	if (defaultOperator && operators.includes(defaultOperator)) return defaultOperator;
	return operators[0] ?? FilterOperator.EQ;
}

export function getDefaultOperatorForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
	defaultOperator?: FilterOperator,
): FilterOperator {
	return resolveDefaultOperator(getNarrowedOperatorsForField(field, spec), defaultOperator);
}
